import { OperationResult, RequestParameters } from '../types/apiTypes';
import type { DirectionVariant } from '../types/gtfsTypes';
import { RidershipDirectionHistoryApiRow, RidershipFiltersCatalogResponse } from '../types/ridershipDirectionHistoryTypes';
import { RidershipDataReadinessResponse } from '../types/ridershipReadinessTypes';
import { AverageWeekdayBoardingBatchRequest, AverageWeekdayBoardingSummaryRecord, AverageWeekdayBoardingTimelineRecord, AverageWeekdayStopSeatCapacityRecord, AverageWeekdayVehicleCapacityRecord, RidershipAggregateApiRow, RidershipStopAggregateApiRow, RidershipStopsRankingByRoutesRequest, RidershipStopSummaryApiRow } from '../types/ridershipTypes';
import { DayTypeFilter, normalizeDayTypeFilter } from '../utils/dayType';
import { to24Hour } from '../utils/time';
import api from './api';
import { buildUrlString } from './helpers';

function normalizeRouteIds(routeIds?: string[]) {
  if (!Array.isArray(routeIds)) {
    return [];
  }
  return Array.from(new Set(routeIds
    .map(routeId => (routeId || '').trim())
    .filter(routeId => routeId.length > 0)));
}

interface RidershipStopActivityOverviewApiResponse {
  stopId: string;
  stopName: string;
  summaries: RidershipStopSummaryApiRow[];
}

interface RidershipStopActivityOverviewByStopsApiResponse {
  summaries: RidershipStopSummaryApiRow[];
  directionVariants: DirectionVariant[];
  stopActivity: RidershipStopAggregateApiRow[];
}

class RidershipApi {
  static base = '/ridership';

  private static normalizeCsvFilterValues(values?: string[]) {
    const normalized = Array.from(new Set((values || [])
      .map(value => (value || '').trim())
      .filter(value => value.length > 0)))
      .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }));
    return normalized.length > 0 ? normalized.join(',') : undefined;
  }

  // Legacy endpoint by trip/vehicle (kept for backward compatibility if server still supports it)
  static async getRidershipHistory(agencyId: string, tripId: string, vehicleId: string) {
    const url = buildUrlString(null, 'RidershipHistory', agencyId, 'trip', tripId, 'vehicle', vehicleId);
    return await api.doAuthGetRequestAsync<OperationResult<RidershipAggregateApiRow[]>>(`${this.base}/${url}`);
  }

  // New endpoint by route and directions (now returns stop-level aggregates)
  static async getRidershipHistoryByRoute(
      agencyId: string,
      routeId: string,
      directions: string,
      fromDate: string,
      toDate: string,
      dayType: DayTypeFilter = 'workdays',
      actualArrivalFrom: string,
      actualArrivalTo: string,
      tripId?: string,
      directionIds?: string[],
      tripIds?: string[],
      parameters?: RequestParameters,
  ) {
    const params = {
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      dayType: normalizeDayTypeFilter(dayType),
      tripId: tripId?.trim() || undefined,
      directionIds: this.normalizeCsvFilterValues(directionIds),
      tripIds: this.normalizeCsvFilterValues(tripIds),
      actualArrivalFrom: to24Hour(actualArrivalFrom),
      actualArrivalTo: to24Hour(actualArrivalTo),
    } as const;
    const url = buildUrlString(params as any, agencyId, 'routes', routeId, 'directions', directions, 'history');
    return await api.doAuthGetRequestAsyncWithParams<OperationResult<RidershipDirectionHistoryApiRow[]>>(`${this.base}/${url}`, parameters);
  }

  static async getRidershipFilterCatalog(agencyId: string, fromDate?: string, toDate?: string, parameters?: RequestParameters) {
    const params = {
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    } as const;
    const url = buildUrlString(params as any, agencyId, 'filters');
    return await api.doAuthGetRequestAsyncWithParams<OperationResult<RidershipFiltersCatalogResponse>>(`${this.base}/${url}`, parameters);
  }

  // Aggregated sums endpoints for ranking (keep legacy typing used by Ranking page)
  static async getRidershipSumByRoute(agencyId: string, fromDate?: string, toDate?: string, dayType?: DayTypeFilter, parameters?: RequestParameters) {
    const params = {
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      dayType: normalizeDayTypeFilter(dayType),
      mode: 'sum',
    } as const;
    const url = buildUrlString(params as any, agencyId, 'routes', 'aggregate');
    return await api.doAuthGetRequestAsyncWithParams<OperationResult<RidershipAggregateApiRow[]>>(`${this.base}/${url}`, parameters);
  }

  static async getRidershipAvgByRoute(agencyId: string, fromDate?: string, toDate?: string, dayType?: DayTypeFilter, parameters?: RequestParameters) {
    const params = {
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      dayType: normalizeDayTypeFilter(dayType),
      mode: 'avg',
    } as const;
    const url = buildUrlString(params as any, agencyId, 'routes', 'aggregate');
    return await api.doAuthGetRequestAsyncWithParams<OperationResult<RidershipAggregateApiRow[]>>(`${this.base}/${url}`, parameters);
  }

  static async getRidershipSumByStop(agencyId: string, fromDate?: string, toDate?: string, dayType?: DayTypeFilter, parameters?: RequestParameters) {
    const params = {
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      dayType: normalizeDayTypeFilter(dayType),
      mode: 'sum',
    } as const;
    const url = buildUrlString(params as any, agencyId, 'stops', 'aggregate');
    return await api.doAuthGetRequestAsyncWithParams<OperationResult<RidershipAggregateApiRow[]>>(`${this.base}/${url}`, parameters);
  }

  static async getRidershipAvgByStop(agencyId: string, fromDate?: string, toDate?: string, dayType?: DayTypeFilter, parameters?: RequestParameters) {
    const params = {
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      dayType: normalizeDayTypeFilter(dayType),
      mode: 'avg',
    } as const;
    const url = buildUrlString(params as any, agencyId, 'stops', 'aggregate');
    return await api.doAuthGetRequestAsyncWithParams<OperationResult<RidershipAggregateApiRow[]>>(`${this.base}/${url}`, parameters);
  }

  static async getRidershipStopSummary(agencyId: string, stopId: string, fromDate?: string, toDate?: string, dayType?: DayTypeFilter, actualArrivalFrom?: string, actualArrivalTo?: string, parameters?: RequestParameters) {
    const params = {
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      dayType: normalizeDayTypeFilter(dayType),
      actualArrivalFrom: to24Hour(actualArrivalFrom),
      actualArrivalTo: to24Hour(actualArrivalTo),
    } as const;
    const url = buildUrlString(params as any, agencyId, 'stops', stopId, 'summary');
    return await api.doAuthGetRequestAsyncWithParams<OperationResult<RidershipStopSummaryApiRow[]>>(`${this.base}/${url}`, parameters);
  }

  static async getRidershipStopActivityOverview(agencyId: string, stopId: string, fromDate?: string, toDate?: string, dayType?: DayTypeFilter, actualArrivalFrom?: string, actualArrivalTo?: string, parameters?: RequestParameters) {
    const params = {
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      dayType: normalizeDayTypeFilter(dayType),
      actualArrivalFrom: to24Hour(actualArrivalFrom),
      actualArrivalTo: to24Hour(actualArrivalTo),
    } as const;
    const url = buildUrlString(params as any, agencyId, 'stops', stopId, 'overview-by-route');
    return await api.doAuthGetRequestAsyncWithParams<OperationResult<RidershipStopActivityOverviewApiResponse>>(`${this.base}/${url}`, parameters);
  }

  static async getRidershipStopActivityOverviewByStops(
    agencyId: string,
    stopIds: string[],
    fromDate?: string,
    toDate?: string,
    dayType?: DayTypeFilter,
    actualArrivalFrom?: string,
    actualArrivalTo?: string,
    parameters?: RequestParameters,
  ) {
    const payload = {
      stopIds: stopIds || [],
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      dayType: normalizeDayTypeFilter(dayType),
      actualArrivalFrom: to24Hour(actualArrivalFrom),
      actualArrivalTo: to24Hour(actualArrivalTo),
    } as const;
    const url = buildUrlString(null, agencyId, 'stops', 'overview-by-route');
    return await api.doAuthPostRequestAsync<typeof payload, OperationResult<RidershipStopActivityOverviewByStopsApiResponse>>(`${this.base}/${url}`, payload, parameters);
  }

  static async getRidershipStopActivityByStops(
    agencyId: string,
    stopIds: string[],
    fromDate?: string,
    toDate?: string,
    dayType?: DayTypeFilter,
    actualArrivalFrom?: string,
    actualArrivalTo?: string,
    parameters?: RequestParameters,
  ) {
    const payload = {
      stopIds: stopIds || [],
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      dayType: normalizeDayTypeFilter(dayType),
      actualArrivalFrom: to24Hour(actualArrivalFrom),
      actualArrivalTo: to24Hour(actualArrivalTo),
    } as const;
    const url = buildUrlString(null, agencyId, 'stops', 'activity-aggregate');
    return await api.doAuthPostRequestAsync<typeof payload, OperationResult<RidershipStopAggregateApiRow[]>>(`${this.base}/${url}`, payload, parameters);
  }

  static async getRidershipStopActivityByRoutes(
    agencyId: string,
    routeIds: string[],
    fromDate?: string,
    toDate?: string,
    dayType?: DayTypeFilter,
    actualArrivalFrom?: string,
    actualArrivalTo?: string,
    parameters?: RequestParameters,
  ) {
    const normalizedActualArrivalFrom = to24Hour(actualArrivalFrom);
    const normalizedActualArrivalTo = to24Hour(actualArrivalTo);
    const payload = {
      routeIds: normalizeRouteIds(routeIds),
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      dayType: normalizeDayTypeFilter(dayType),
      ...(normalizedActualArrivalFrom ? { actualArrivalFrom: normalizedActualArrivalFrom } : {}),
      ...(normalizedActualArrivalTo ? { actualArrivalTo: normalizedActualArrivalTo } : {}),
    } as const;
    const url = buildUrlString(null, agencyId, 'routes', 'activity-aggregate');
    return await api.doAuthPostRequestAsync<typeof payload, OperationResult<RidershipStopAggregateApiRow[]>>(`${this.base}/${url}`, payload, parameters);
  }

  static async getRidershipStopsRankingByRoutes(
    agencyId: string,
    payload: RidershipStopsRankingByRoutesRequest,
    parameters?: RequestParameters,
  ) {
    const requestBody: RidershipStopsRankingByRoutesRequest = {
      routeIds: normalizeRouteIds(payload?.routeIds),
      fromDate: payload?.fromDate || undefined,
      toDate: payload?.toDate || undefined,
      dayType: normalizeDayTypeFilter(payload?.dayType as DayTypeFilter),
      aggregation: payload?.aggregation === 'avg' ? 'avg' : 'sum',
    };
    const url = buildUrlString(null, agencyId, 'stops', 'ranking-by-routes');
    return await api.doAuthPostRequestAsync<RidershipStopsRankingByRoutesRequest, OperationResult<RidershipAggregateApiRow[]>>(`${this.base}/${url}`, requestBody, parameters);
  }

  static async getAverageWeekdayBoardingSummary(
    agencyId: string,
    routeId: string,
    dayType: DayTypeFilter = 'workdays',
    aggregation: 'sum' | 'avg' = 'sum',
    parameters?: RequestParameters,
  ) {
    const params = {
      dayType: normalizeDayTypeFilter(dayType),
      aggregation: aggregation === 'avg' ? 'avg' : 'sum',
    } as const;
    const url = buildUrlString(params as any, agencyId, 'routes', routeId, 'weekday-boarding-summary');
    return await api.doAuthGetRequestAsyncWithParams<OperationResult<AverageWeekdayBoardingSummaryRecord[]>>(`${this.base}/${url}`, parameters);
  }

  static async getAverageWeekdayBoardingSummaryBatch(
    agencyId: string,
    payload: AverageWeekdayBoardingBatchRequest,
    parameters?: RequestParameters,
  ) {
    const requestBody: AverageWeekdayBoardingBatchRequest = {
      routeIds: payload?.routeIds || [],
      dayType: normalizeDayTypeFilter(payload?.dayType as DayTypeFilter),
      aggregation: payload?.aggregation === 'avg' ? 'avg' : 'sum',
    };
    const url = buildUrlString(null, agencyId, 'routes', 'weekday-boarding-summary');
    return await api.doAuthPostRequestAsync<AverageWeekdayBoardingBatchRequest, OperationResult<AverageWeekdayBoardingSummaryRecord[]>>(`${this.base}/${url}`, requestBody, parameters);
  }

  static async getAverageWeekdayBoardingTimeline(
    agencyId: string,
    routeId: string,
    dayType: DayTypeFilter = 'workdays',
    aggregation: 'sum' | 'avg' = 'sum',
    parameters?: RequestParameters,
  ) {
    const params = {
      dayType: normalizeDayTypeFilter(dayType),
      aggregation: aggregation === 'avg' ? 'avg' : 'sum',
    } as const;
    const url = buildUrlString(params as any, agencyId, 'routes', routeId, 'weekday-boarding-timeline');
    return await api.doAuthGetRequestAsyncWithParams<OperationResult<AverageWeekdayBoardingTimelineRecord[]>>(`${this.base}/${url}`, parameters);
  }

  static async getAverageWeekdayBoardingTimelineBatch(
    agencyId: string,
    payload: AverageWeekdayBoardingBatchRequest,
    parameters?: RequestParameters,
  ) {
    const requestBody: AverageWeekdayBoardingBatchRequest = {
      routeIds: payload?.routeIds || [],
      dayType: normalizeDayTypeFilter(payload?.dayType as DayTypeFilter),
      aggregation: payload?.aggregation === 'avg' ? 'avg' : 'sum',
    };
    const url = buildUrlString(null, agencyId, 'routes', 'weekday-boarding-timeline');
    return await api.doAuthPostRequestAsync<AverageWeekdayBoardingBatchRequest, OperationResult<AverageWeekdayBoardingTimelineRecord[]>>(`${this.base}/${url}`, requestBody, parameters);
  }

  static async getAverageWeekdayVehicleCapacity(
    agencyId: string,
    routeId: string,
    direction: string,
    fromDate?: string,
    toDate?: string,
    dayType: 'any' | 'workdays' | 'saturdays' | 'sundays' | 'weekends' = 'workdays',
    tripId?: string,
    directionIds?: string[],
    tripIds?: string[],
    actualArrivalFrom?: string,
    actualArrivalTo?: string,
      parameters?: RequestParameters,
  ) {
    if (!direction || !direction.trim()) {
      throw new Error('direction is required');
    }
    const dir = direction.trim();
    const params = {
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      dayType: normalizeDayTypeFilter(dayType),
      tripId: tripId?.trim() || undefined,
      directionIds: this.normalizeCsvFilterValues(directionIds),
      tripIds: this.normalizeCsvFilterValues(tripIds),
      actualArrivalFrom: to24Hour(actualArrivalFrom),
      actualArrivalTo: to24Hour(actualArrivalTo),
    } as const;
    const url = buildUrlString(params as any, agencyId, 'routes', routeId, 'directions', dir, 'weekday-vehicle-capacity');
    return await api.doAuthGetRequestAsyncWithParams<OperationResult<AverageWeekdayVehicleCapacityRecord[]>>(`${this.base}/${url}`, parameters);
  }

  static async getAverageWeekdayStopSeatCapacity(
    agencyId: string,
    routeId: string,
    direction: string,
    fromDate?: string,
    toDate?: string,
    dayType: 'any' | 'workdays' | 'saturdays' | 'sundays' | 'weekends' = 'workdays',
    tripId?: string,
    directionIds?: string[],
    tripIds?: string[],
    actualArrivalFrom?: string,
    actualArrivalTo?: string,
      parameters?: RequestParameters,
  ) {
    if (!direction || !direction.trim()) {
      throw new Error('direction is required');
    }
    const dir = direction.trim();
    const params = {
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      dayType: normalizeDayTypeFilter(dayType),
      tripId: tripId?.trim() || undefined,
      directionIds: this.normalizeCsvFilterValues(directionIds),
      tripIds: this.normalizeCsvFilterValues(tripIds),
      actualArrivalFrom: to24Hour(actualArrivalFrom),
      actualArrivalTo: to24Hour(actualArrivalTo),
    } as const;
    const url = buildUrlString(params as any, agencyId, 'routes', routeId, 'directions', dir, 'weekday-stop-seat-capacity');
    return await api.doAuthGetRequestAsyncWithParams<OperationResult<AverageWeekdayStopSeatCapacityRecord[]>>(`${this.base}/${url}`, parameters);
  }

  // Returns list of routeIds that are of type Light Rail
  static async getLightRailRouteIds(agencyId: string) {
    const url = buildUrlString(null, agencyId, 'routes', 'light-rail-ids');
    return await api.doAuthGetRequestAsync<OperationResult<string[]>>(`${this.base}/${url}`);
  }

  static async getRidershipDataReadiness(
    agencyId: string,
    fiscalYear?: number,
    dailyFrom?: string,
    dailyTo?: string,
    parameters?: RequestParameters,
  ) {
    const params = {
      fiscalYear: fiscalYear != null ? String(fiscalYear) : undefined,
      dailyFrom,
      dailyTo,
    } as const;
    const url = buildUrlString(params as Record<string, string | undefined>, agencyId, 'data-sync-status');
    return await api.doAuthGetRequestAsyncWithParams<OperationResult<RidershipDataReadinessResponse>>(`${this.base}/${url}`, parameters);
  }
}

export default RidershipApi;
